import '$core/TopUpBalanceSheet/TopUpBalanceSheet.sass';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Form, FormItem, Header, List, Placeholder, ScrollView } from '$uikit';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { formatInputAmount } from '$utils/number';
import { AmountInput, Loader } from '$shared/components';
import { CurrencyEnum } from '$store/models';
import { goBack } from '$navigation/helper';
import WebApp from '@twa-dev/sdk';
import { payActions, paySelector } from '$store/pay';
import { mainSelector } from '$store/main';
import { TopUpMethod } from '$store/pay/interface';
import { IcBankCard28, IcDiscover56, IcJcb56, IcMastercard56, IcVisa56 } from '$assets';
import {
  getMethodVisual,
  getPayErrorMessage,
} from '$core/TopUpBalanceSheet/getMethodVisual';

const MIN_AMOUNT = 10;
const MAX_TON_AMOUNT = 500;
const MAX_EUR_AMOUNT = 500;
const AMOUNT_PRESETS = [10, 25, 50, 100];

export const RefillTon: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const user = useCurrentUserInfo();
  const { topUpMethods } = useSelector(paySelector);
  const { countryCode } = useSelector(mainSelector);

  const [amount, setAmount] = useState(`${AMOUNT_PRESETS[1]}`);
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [tonRate, setTonRate] = useState(0);
  const [isCustomAmountMode, setCustomAmountMode] = useState(false);

  const loadRate = useCallback(() => {
    setLoadingStatus('loading');
    Api.get('/rates/convert/ton')
      .then((resp) => {
        const data: any = resp;
        setTonRate(data.rate * 1.1);
        setLoadingStatus('');
      })
      .catch((err) => {
        dispatch(toastActions.setToastFail(err.message));
        setLoadingStatus('failed');
      });
  }, [dispatch]);

  useEffect(() => {
    loadRate();
    dispatch(payActions.loadTopUpMethods());
  }, [dispatch, loadRate]);

  const maxTonAmount = useMemo(() => {
    if (tonRate <= 0) {
      return MAX_TON_AMOUNT;
    }
    return Math.min(MAX_TON_AMOUNT, Math.floor(MAX_EUR_AMOUNT / tonRate));
  }, [tonRate]);

  const amountTip = useMemo(() => {
    if (+amount < MIN_AMOUNT) {
      return t('buy_ton_min_label', {
        amount: MIN_AMOUNT,
      });
    }

    if (+amount > maxTonAmount) {
      return t('buy_ton_max_label');
    }

    return t('buy_ton_pay_label', {
      amount: formatInputAmount(`${+amount * tonRate}`, 2),
    });
  }, [tonRate, amount, maxTonAmount, t]);

  const groupedMethods = useMemo(() => {
    const groups: Record<string, TopUpMethod[]> = {};
    for (const m of topUpMethods) {
      if (!groups[m.region]) groups[m.region] = [];
      groups[m.region].push(m);
    }
    for (const region of Object.keys(groups)) {
      groups[region].sort((a, b) =>
        a.recommended === b.recommended ? 0 : a.recommended ? -1 : 1,
      );
    }
    return Object.keys(groups)
      .sort((a, b) => {
        const aHasRec = groups[a].some((m) => m.recommended);
        const bHasRec = groups[b].some((m) => m.recommended);
        if (aHasRec !== bHasRec) return aHasRec ? -1 : 1;
        if (a === 'ALL' && b !== 'ALL') return 1;
        if (b === 'ALL' && a !== 'ALL') return -1;
        return 0;
      })
      .map((region) => ({ region, methods: groups[region] }));
  }, [topUpMethods]);

  const handleSend = useCallback(
    (m: TopUpMethod) => {
      if (+amount < MIN_AMOUNT || +amount > maxTonAmount) {
        return;
      }
      dispatch(toastActions.setToastLoading());
      Api.post<any, any>('/pay/buy', {
        amount: +amount,
        paymentMethod: m.method,
        countryCode,
      })
        .then((data) => {
          if (window.isWebApp) {
            goBack();
            WebApp.openLink(data.url);
            dispatch(toastActions.hideToast());
          } else {
            location.href = data.url;
          }
        })
        .catch((err) => {
          dispatch(toastActions.setToastFail(getPayErrorMessage(err, t)));
        });
    },
    [amount, countryCode, dispatch, maxTonAmount, t],
  );

  const getRegionTitle = (region: string): string => {
    switch (region) {
      case 'ALL':
        return t('balance_for_world_title');
      case 'RU':
        return t('balance_for_russia_title');
      case 'BR':
        return t('balance_for_brazil_title');
      case 'MX':
        return t('balance_for_mexico_title');
      default:
        return region;
    }
  };

  const renderSmallMethodButton = (m: TopUpMethod) => {
    const visual = getMethodVisual(m.method, t);
    return (
      <div
        key={m.method}
        className={`TopUpBalanceSheet__button${visual.large ? ' mir' : ''}`}
        onClick={() => handleSend(m)}
        data-payment-method={m.method}
        data-recommended={m.recommended || undefined}
      >
        <div className="TopUpBalanceSheet__button_icon">{visual.icon}</div>
        <div className="TopUpBalanceSheet__button_label">{visual.label}</div>
      </div>
    );
  };

  const renderWorldGroup = (methods: TopUpMethod[]) => {
    const card = methods.find((m) => m.method === 'card');
    const smalls = methods.filter((m) => m.method !== 'card');

    return (
      <List key="ALL" title={t('balance_for_world_title')}>
        <div className="TopUpBalanceSheet__methods">
          {card && (
            <Button
              onClick={() => handleSend(card)}
              className="TopUpBalanceSheet__cardBtn"
              iconRight={
                <>
                  <IcVisa56 />
                  <IcMastercard56 />
                  <IcJcb56 />
                  <IcDiscover56 />
                </>
              }
              mode="pay"
              data-payment-method={card.method}
              data-recommended={card.recommended || undefined}
            >
              <IcBankCard28 />
              {t('top_up_method_singularity')}
            </Button>
          )}
          {smalls.length > 0 && (
            <div className="TopUpBalanceSheet_group">
              {smalls.map(renderSmallMethodButton)}
            </div>
          )}
        </div>
      </List>
    );
  };

  const renderRegionGroup = (region: string, methods: TopUpMethod[]) => (
    <List key={region} title={getRegionTitle(region)}>
      <div className="TopUpBalanceSheet__methods">
        <div className="TopUpBalanceSheet_group">
          {methods.map(renderSmallMethodButton)}
        </div>
      </div>
    </List>
  );

  const renderDynamicMethods = () => {
    if (groupedMethods.length === 0) {
      return null;
    }
    return (
      <>
        {groupedMethods.map(({ region, methods }) =>
          region === 'ALL'
            ? renderWorldGroup(methods)
            : renderRegionGroup(region, methods),
        )}
      </>
    );
  };

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed' || tonRate <= 0) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadRate}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <ScrollView>
        <Form>
          <FormItem>
            <AmountInput
              amount={amount}
              onInputChange={setAmount}
              amountList={AMOUNT_PRESETS}
              currency={CurrencyEnum.TON}
              title={t('exchange_eur_amount')}
              inputTip={amountTip}
              placeholder={t('exchange_eur_amount_placeholder')}
              isCustomAmountMode={isCustomAmountMode}
              onInlineButtonClick={() => setCustomAmountMode(true)}
            />
          </FormItem>
          <div className="PaySheet__button_wrap">{renderDynamicMethods()}</div>
        </Form>
      </ScrollView>
    );
  }

  if (user?.verified !== 2) {
    return <h1>404</h1>;
  }

  return (
    <>
      <Header showBackButton>{t('buy_ton_title')}</Header>
      {renderContent()}
    </>
  );
};
